import {
  Accordion,
  Code,
  Loader,
  SimpleGrid,
  Stack,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Bug } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../shared/components/PageHeader";
import { DocumentTypeActionCard } from "./DocumentTypeActionCard";
import { OrderTrackingActionCard } from "./OrderTrackingActionCard";
import { WelcomeRequestFeed } from "./WelcomeRequestFeed";
import type { WelcomeDashboardProps } from "./WelcomeDashboard";
import styles from "./WelcomePage.module.css";

export function WelcomeAdminView({
  userName,
  recentRequests,
  documentTypes,
  requestsBasePath,
  jwtPayload,
  isLoadingRequests,
  orderTrackingHref,
}: WelcomeDashboardProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 48em)");

  return (
    <Stack
      gap={isMobile ? "md" : "xl"}
      py={{ base: "md", sm: "xl" }}
      data-tour-id="welcome-dashboard"
    >
      <PageHeader
        title={t("welcome.title", { name: userName })}
        subtitle={t("welcome.subtitle")}
      />

      {(documentTypes.length > 0 || orderTrackingHref) && (
        <Stack gap="sm">
          <Title
            order={2}
            size="h5"
            fw={600}
            tt="uppercase"
            c="dimmed"
            lts="0.08em"
          >
            {t("welcome.quickActions")}
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {documentTypes.map((dt) => (
              <DocumentTypeActionCard key={dt.id} docType={dt} />
            ))}
            {orderTrackingHref ? (
              <OrderTrackingActionCard href={orderTrackingHref} />
            ) : null}
          </SimpleGrid>
        </Stack>
      )}

      <Stack gap="sm">
        <Title
          order={2}
          size="h5"
          fw={600}
          tt="uppercase"
          c="dimmed"
          lts="0.08em"
        >
          {t("welcome.recentActivity")}
        </Title>
        <WelcomeRequestFeed
          requests={recentRequests}
          requestsBasePath={requestsBasePath}
          isLoading={isLoadingRequests}
        />
      </Stack>

      <Accordion variant="separated" radius="xs">
        <Accordion.Item value="debug">
          <Accordion.Control icon={<Bug size={16} />}>
            {t("welcome.systemDiagnostics")}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Title order={3} size="xs" c="dimmed" fw={700} tt="uppercase">
                JWT Payload
              </Title>
              {jwtPayload !== undefined ? (
                <Code
                  block
                  className={styles.codeBlock}
                  data-testid="welcome-jwt-code"
                >
                  {JSON.stringify(jwtPayload, null, 2)}
                </Code>
              ) : (
                <Loader size="sm" />
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
